import React, {
	PropsWithChildren,
	memo,
	useCallback,
	useMemo,
	useState,
	useEffect,
} from "react";

import { Map } from "../../../../../../services";
import { StyledP, Popup } from "../../../../../common";
import {
	ValueAccessors,
	usePointOrderModalTableContext,
	Provider,
} from "../../context";
import PointOrderModalTableBase from "../..";
import { Settlement } from "../Settlement";
import BaseStreet, { Street } from "../Street";
import { House } from "../House";
import { Entrance } from "../Entrance";
import { Setting } from "../Setting";
import { HandleEventsProps } from "../../hooks";

const WrapItemBase: React.FC<WrapItemBase.Props> = ({
	rowData,
	rowIndex,
	dataKey,
	colIndex,
	width,
	setData,
	disabled,
	customerId,
	orderId,
	height,
	containerId,
	onDelete,
	onAddRow,
	countRow,
	searchTypes,
	taxiServiceId,
	loadingChangePoint,
	setLoadingChangePoint,
}) => {
	const {
		addStreet,
		handleChangeStreet,
		getData,
		addSettlement,
		reload,
		reloadCell,
		setReloadCell,
		rowId,
		reset,
		addSettlementCoordinates,
		addHouseCoordinates,
	} = usePointOrderModalTableContext();

	const idCel = useMemo(() => rowData.raw.id, [rowData.raw.id]);
	const defaultPayload = useMemo<Provider.GetData>(
		() => ({
			street: undefined,
			settlement: undefined,
			settlementCoordinates: undefined,
			streetCoordinates: undefined,
			houseCoordinates: undefined,
			hadChangeStreet: undefined,
			cityCellState: {
				isManual: false,
				isSelected: false,
			},
			streetCellState: {
				isManual: false,
				isSelected: false,
			},
		}),
		[],
	);

	const [payload, setPayload] = useState<Provider.GetData>(defaultPayload);

	const removeRow = useCallback(
		(id: number) => {
			if (onDelete) onDelete(id);
			reset(id);
			setPayload(defaultPayload);
		},
		[onDelete, reset, defaultPayload],
	);

	useEffect(() => {
		if (!rowData.raw.type || !rowData.raw.saveType) {
			setPayload({
				...defaultPayload,
				settlement: rowData.raw as unknown as Map.Search.Object,
				settlementCoordinates: rowData.raw.coordinates,
			});
		}
	}, [defaultPayload, rowData.raw]);

	useEffect(() => {
		if (reload && idCel === rowId) {
			const data = getData(idCel);
			setPayload(data);
		}
	}, [getData, reload, idCel, rowId]);

	const offset = useMemo<Popup.Props["offset"]>(() => ({ x: 0, y: 31 }), []);
	const popoverWidth = useMemo(() => {
		if (width) return `${width}px`;
		return "50px";
	}, [width]);

	const styles = useMemo<Popup.Styles>(
		() => ({
			h: { max: "200px", min: "0px" },
			w: { min: popoverWidth },
		}),
		[popoverWidth],
	);

	const firstCity = useMemo<HandleEventsProps>(
		() => ({
			current: `${idCel}city`,
			nextElement: `${idCel}street`,
			arrowRight: `${idCel}street`,
			arrowLeft:
				idCel === 1 ? `customer-phone-id` : `${idCel - 1}entrance`,
		}),
		[idCel],
	);

	const streetOption = useMemo<HandleEventsProps>(
		() => ({
			current: `${idCel}street`,
			nextElement: `${idCel}house`,
			arrowLeft: `${idCel}city`,
			arrowRight: `${idCel}house`,
		}),
		[idCel],
	);

	const houseOnSaveBtn = useMemo(
		() => (countRow === 1 ? `${idCel}entrance` : `order-save-id`),
		[countRow, idCel],
	);
	const lastHouse = useMemo<HandleEventsProps>(
		() => ({
			current: `${idCel}house`,
			nextElement: countRow === idCel ? "" : `${idCel}entrance`,
			externalElement: countRow === idCel ? `order-save-id` : "",
			arrowLeft: `${idCel}street`,
			arrowRight: `${idCel}entrance`,
			fol: countRow === idCel ? houseOnSaveBtn : `${idCel + 1}street`,
		}),
		[countRow, idCel, houseOnSaveBtn],
	);

	const lastEntrance = useMemo<HandleEventsProps>(
		() => ({
			labels: ["entrance"],
			nextElement: `${idCel + 1}street`,
			externalElement:
				countRow === idCel ? "order-save-id" : `${idCel + 1}street`,
			arrowLeft: `${idCel}house`,
			arrowRight:
				countRow === idCel ? "driver-node-id" : `${idCel + 1}street`,
		}),
		[countRow, idCel],
	);

	const settingSwitch = useMemo(
		() => ({
			nextElement: `${idCel + 1}street`,
			externalElement: "order-save-id",
			initialElement: `${idCel}street`,
		}),
		[idCel],
	);

	const element = useMemo(() => {
		if (disabled || loadingChangePoint) {
			return (
				<StyledP
					w="100%"
					h="100%"
					justify="start"
					p="0 6px"
					alignItems="center"
					textAlign="left"
					colors="#21333f"
					font={{
						ff: "Inter Lato",
						fw: "400",
						size: "12px",
						line: "16px",
					}}
					text={{ tt: "uppercase" }}
				>{`${rowData[dataKey] || ""}`}</StyledP>
			);
		}

		switch (dataKey as ValueAccessors) {
			case "city":
				return (
					<Settlement
						height={height}
						rowIndex={rowIndex}
						rowData={rowData}
						dataKey={dataKey}
						colIndex={colIndex}
						offset={offset}
						styles={styles}
						setData={setData}
						customerId={customerId}
						containerId={containerId}
						addSettlement={addSettlement}
						addSettlementCoordinates={addSettlementCoordinates}
						handleEventsOptions={firstCity}
						countRow={countRow}
						reloadCell={reloadCell}
						setReloadCell={setReloadCell}
						loadingChangePoint={loadingChangePoint}
						street={payload.street}
						setLoadingChangePoint={setLoadingChangePoint}
					/>
				);

			case "street":
				return (
					<Street
						height={height}
						rowIndex={rowIndex}
						rowData={rowData}
						dataKey={dataKey}
						colIndex={colIndex}
						offset={offset}
						styles={styles}
						setData={setData}
						customerId={customerId}
						orderId={orderId}
						containerId={containerId}
						addStreet={addStreet}
						handleChangeStreet={handleChangeStreet}
						settlementCoordinates={payload.settlementCoordinates}
						settlement={payload.settlement}
						street={payload.street}
						addSettlement={addSettlement}
						addSettlementCoordinates={addSettlementCoordinates}
						handleEventsOptions={streetOption}
						searchTypes={searchTypes}
						taxiServiceId={taxiServiceId}
						countRow={countRow}
						reloadCell={reloadCell}
						setReloadCell={setReloadCell}
						loadingChangePoint={loadingChangePoint}
						setLoadingChangePoint={setLoadingChangePoint}
					/>
				);

			case "house":
				return (
					<House
						height={height}
						rowIndex={rowIndex}
						rowData={rowData}
						dataKey={dataKey}
						colIndex={colIndex}
						offset={offset}
						styles={styles}
						street={payload.street}
						setData={setData}
						customerId={customerId}
						containerId={containerId}
						handleEventsOptions={lastHouse}
						addHouseCoordinates={addHouseCoordinates}
						countRow={countRow}
						reloadCell={reloadCell}
						setReloadCell={setReloadCell}
						loadingChangePoint={loadingChangePoint}
						setLoadingChangePoint={setLoadingChangePoint}
						taxiServiceId={taxiServiceId}
					/>
				);

			case "entrance":
				return (
					<Entrance
						height={height}
						rowIndex={rowIndex}
						rowData={rowData}
						dataKey={dataKey}
						colIndex={colIndex}
						setData={setData}
						handleEventsOptions={lastEntrance}
						countRow={countRow}
						reloadCell={reloadCell}
						setReloadCell={setReloadCell}
						loadingChangePoint={loadingChangePoint}
						setLoadingChangePoint={setLoadingChangePoint}
					/>
				);

			case "polygon":
				return (
					<StyledP
						w="100%"
						h={`${height}px`}
						justify="start"
						p="0 6px"
						alignItems="center"
						textAlign="left"
						colors="#21333f"
						font={{
							ff: "Inter Lato",
							fw: "400",
							size: "12px",
							line: "16px",
						}}
						text={{ tt: "uppercase" }}
					>
						{`${rowData[dataKey] || ""}`}
					</StyledP>
				);

			case "setting":
				return (
					<Setting
						rowData={rowData}
						onDelete={removeRow}
						onAddRow={onAddRow}
						countRow={countRow}
						disabled={disabled}
						handleEventsOptions={settingSwitch}
					/>
				);

			default:
				return (
					<StyledP
						w="100%"
						h={`${height}px`}
						justify="start"
						p="0 6px"
						alignItems="center"
						textAlign="left"
						colors="#21333f"
						font={{
							ff: "Inter Lato",
							fw: "400",
							size: "12px",
							line: "16px",
						}}
					>
						{`${rowData[dataKey] || ""}`}
					</StyledP>
				);
		}
	}, [
		disabled,
		dataKey,
		rowData,
		height,
		rowIndex,
		colIndex,
		offset,
		styles,
		setData,
		customerId,
		orderId,
		containerId,
		addSettlement,
		addSettlementCoordinates,
		firstCity,
		countRow,
		addStreet,
		handleChangeStreet,
		payload.settlementCoordinates,
		payload.settlement,
		payload.street,
		streetOption,
		searchTypes,
		taxiServiceId,
		lastHouse,
		addHouseCoordinates,
		lastEntrance,
		removeRow,
		onAddRow,
		settingSwitch,
		reloadCell,
		setReloadCell,
		loadingChangePoint,
		setLoadingChangePoint,
	]);

	return <>{element}</>;
};

declare namespace WrapItemBase {
	interface Props extends PropsWithChildren {
		rowData: PointOrderModalTableBase.OrderModal;
		setData: (data: PointOrderModalTableBase.OrderModal) => void;
		loadingChangePoint: boolean;
		setLoadingChangePoint: React.Dispatch<React.SetStateAction<boolean>>;
		onAddRow?: (num: number) => void;
		onDelete?: (id: number) => void;
		rowIndex?: number;
		dataKey: ValueAccessors | string;
		colIndex: number;
		width?: number;
		height?: number;
		disabled?: boolean;
		customerId?: number;
		orderId?: number;
		containerId?: string;
		countRow: number;
		searchTypes?: BaseStreet.Props["searchTypes"];
		taxiServiceId?: BaseStreet.Props["taxiServiceId"];
	}
}

export const WrapItem = memo(WrapItemBase);

export default WrapItemBase;
